import { useEffect, useMemo } from 'react';

import { FormikValues, useFormik } from 'formik';

import {
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  getLongTermContractAttributes,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
  resetLongTermContractAttributes,
  TravelAllowanceEnums,
} from '@pharmaplan/common';
import {
  getDaysDistribution,
  handleWorkshiftToggles,
} from '@pharmaplan/common/helpers/utils';

import { IApproveBooking } from '../../components/Admin/ApproveBooking/ApproveBooking';
import Validator, {
  SelfApproveValidator,
} from '../../components/Admin/ApproveBooking/validator';
import {
  convertToNullProxy,
  decimalConverterProxy,
  handlerForZeroes,
  parseEmptyStringProxy,
  parseFloatProxy,
} from '../../helpers/proxyHandlers';
import { OtherScreens } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';

import useApproveBookingUtils from './useApproveBookingUtils';
import useAdminBookingBlock from '../Admin/useAdminBookingBlock';
import useSyncWorkshiftValues from '../workshift/useSyncWorkshiftValues';
import useSelfService from '../useSelfService';
import {
  getLongTermHours,
  rateLongTermParamsValidation,
  rateValidation,
} from '../../helpers/Functions';
import { useAppSelector } from '../useAppSelector';
import useGetLongTermDistributions from '../PharmacistProfile/useGetLongTermDistributions';
import useApproveBookingDerivedValues from './useApproveBookingDerivedValues';

const {
  accommodationAllowanceType: accommodationAllowanceTypeEnum,
  travelAllowanceType,
  travelAllowanceRatePerKm: travelAllowanceRatePerKmEnum,
  travelAllowanceAmount: travelAllowanceAmountEnum,
  mealAllowance: mealAllowanceEnum,
  accommodationAllowance: accommodationAllowanceEnum,
  emergencyFees: emergencyFeesEnum,
  otherAllowance: otherAllowanceEnum,
} = AllowancesNames;

const { accommodation, meal, travel, emergency, others } = AllowanceEnums;

const useApproveBooking = ({
  workshiftId,
  bookingId,
  fromScreen,
  type,
  eventIds,
  isAutoSyncRecord,
  back,
  professionalId,
  longTermShiftContractId,
}: IApproveBooking) => {
  const dispatch = useAppDispatch();
  const { actionRequiredDialog, disableActions } = useAdminBookingBlock();
  const { isSelfService } = useSelfService();

  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { shiftContractAttributes } = longTermAttributes ?? {};

  const { weekdayHours, weekendHours } = useMemo(
    () =>
      getDaysDistribution(shiftContractAttributes),
    [shiftContractAttributes],
  );

  const { details } = useApproveBookingUtils({
    workshiftId,
    eventIds,
    bookingId,
    fromScreen,
    type,
    back,
    professionalId,
    isAutoSyncRecord,
    longTermShiftContractId,
  });

  const {
    mealAllowance,
    emergencyFees,
    pharmaId,
    accommodationAllowanceType,
    travelAllowanceTypes,
    counterAccommodationAllowanceType,
    counterEndDate,
    counterMealAllowance,
    counterStartDate,
    counterTravelAllowanceType,
    startDate,
    endDate,
    counterEmergencyFees,
    otherAllowance,
    creditsLeft: singleCreditsLeft,
    professionalCreditsLeft: singleProfessionalCreditsLeft,
    notes,
  } = (details as IAdminApproveDetailsData) ?? {};

  const {
    longTermShiftContract,
    counterShiftContract,
    creditsLeft: multiCreditsLeft,
    professionalCreditsLeft: multiProfessionalCreditsLeft,
    promoHourlyRate: multiPromoHourlyRate,
    requests,
    counterRequests,
    totalWeekHours,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    totalWeekendHours,
  } = details as IAdminRequestDetailsContractModel;

  const {
    allowanceNotes: counterAllowanceNotes,
  } = counterShiftContract ?? {};

  const {
    approveData,
    approveContract,
    creditsLeft,
    hourlyBonus,
    multiHourlyBonus,
    pharmacyInfoText,
    professionalCreditsLeft,
    professionalInfoText,
    submitBookingApproval,
  } = useApproveBookingDerivedValues({
    bookingId,
    details,
    dispatch,
    isSelfService,
    longTermShiftContractId,
    multiCreditsLeft,
    multiProfessionalCreditsLeft,
    singleCreditsLeft,
    singleProfessionalCreditsLeft,
    workshiftId,
  });

  const {
    allowanceNotes,
    pharmacyWeekDayPaidHourlyRate,
    pharmacyWeekendPaidHourlyRate,
  } = longTermShiftContract ?? {};

  const pharmacyWeekday = pharmacyWeekDayPaidHourlyRate || pharmacyWeekDayHourlyRate;
  const pharmacyWeekend = pharmacyWeekendPaidHourlyRate || pharmacyWeekendHourlyRate;

  const { totalShifts } = useGetLongTermDistributions(
    requests,
    counterRequests,
    requests?.length,
  );

  const allowanceTypes = {
    travelAllowanceTypes: travelAllowanceTypes?.length
      ? travelAllowanceTypes
      : counterTravelAllowanceType,
  };

  const approveDates = {
    startDate: counterStartDate ?? startDate,
    endDate: counterEndDate ?? endDate,
  };

  const isBookAvailability = fromScreen === OtherScreens.MatchingAvailabilities
    || fromScreen === OtherScreens.MatchingWorkshifts;

  const onSubmit = (values: FormikValues) => {
    if (!isAutoSyncRecord && disableActions) {
      actionRequiredDialog();
      return;
    }

    const val = {
      travelAllowance: values?.travelAllowance,
      pharmacyHourlyRate: values?.pharmacyHourlyRate,
      pharmacistHourlyRate: values?.pharmacistHourlyRate,
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKmEnum],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmountEnum],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal] || !!isAutoSyncRecord,
        values[mealAllowanceEnum],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceTypeEnum]
          === AllowanceTypesToHandle.paidValuePerNight || !!isAutoSyncRecord,
        values[accommodationAllowanceEnum],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency] || !!isAutoSyncRecord,
        values[emergencyFeesEnum],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowanceEnum],
        null,
      ),
    };

    const {
      pharmaId: formikPharmaId,
      notesForSalesOrder,
      sendEmailToPharmacist,
      sendEmailToPharmacy,
      replacementType,
    } = values ?? {};

    const handledForDecimals = new Proxy(val, parseFloatProxy);
    const handledForNull = new Proxy(handledForDecimals, convertToNullProxy);

    const formikValues = {
      isAutoSyncRecord,
      ...handledForNull,
      bookingId,
      notes: values?.notes,
      pharmaId: formikPharmaId,
      notesForSalesOrder,
      sendEmailToPharmacist,
      sendEmailToPharmacy,
      sendEmailToProfessional: sendEmailToPharmacist,
      travelAllowanceTypes: values[travelAllowanceType],
      accommodationAllowanceType: handleWorkshiftToggles(
        values[accommodation],
        values[accommodationAllowanceTypeEnum],
        null,
      ),
    };

    if (longTermShiftContractId) {
      const rates = {
        weekDayPaidHourlyRate:
          Number(values?.weekDayPaidHourlyRate ?? 0) + Number(hourlyBonus),
        weekendPaidHourlyRate:
          Number(values?.weekendPaidHourlyRate ?? 0) + Number(hourlyBonus),
        pharmacyWeekDayHourlyRate: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekendHourlyRate: values?.pharmacyWeekendHourlyRate,
      };

      const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);
      const handledForFloat = new Proxy(handledForEmptyString, parseFloatProxy);

      const isPharmacyLongParamsGreater = rateLongTermParamsValidation({
        professionalWeekday: values?.weekDayPaidHourlyRate,
        professionalWeekend: values?.weekendPaidHourlyRate,
        pharmacyWeekday: values?.pharmacyWeekDayHourlyRate,
        pharmacyWeekend: values?.pharmacyWeekendHourlyRate,
        dispatch,
        isSelfService:
          isSelfService || !!creditsLeft || !!professionalCreditsLeft,
        weekdayHours,
        replacementType,
        weekendHours,
      });

      if (!isPharmacyLongParamsGreater) {
        return;
      }

      dispatch(
        approveContract({
          notesForSalesOrder: values?.notesForSalesOrder,
          longTermContractId: longTermShiftContractId,
          professionalId: professionalId ?? '',
          notes: values?.notes,
          allowanceNotes: values?.allowanceNotes,
          ...handledForFloat,
        }),
      );

      return;
    }

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: handledForDecimals.pharmacistHourlyRate,
      pharmacyRate: handledForDecimals.pharmacyHourlyRate,
      isSelfService:
        isSelfService || !!creditsLeft || !!professionalCreditsLeft,
      dispatch,
      replacementType,
    });

    if (!isPharmacyGreaterPharmacist) {
      return;
    }

    submitBookingApproval(isBookAvailability, formikValues);
  };

  const handledForZero = new Proxy(approveData, handlerForZeroes);
  const handledForDecimals = new Proxy(handledForZero, decimalConverterProxy);

  const formik = useFormik({
    initialValues: {
      ...allowanceTypes,
      ...handledForDecimals,
      ...approveDates,
      pharmaId,
      contractType: longTermShiftContractId
        ? ContractTypes.longTerm
        : ContractTypes.shortTerm,
      notesForSalesOrder: '',
      notes,
      weekdayHours: totalWeekHours,
      weekendHours: totalWeekendHours,
      allowanceNotes: counterAllowanceNotes || allowanceNotes,
      sendEmailToPharmacist: false,
      sendEmailToPharmacy: false,
      [accommodationAllowanceTypeEnum]: `${counterAccommodationAllowanceType || accommodationAllowanceType}`,
      [AllowancesNames.travelAllowanceType]:
        (travelAllowanceTypes?.length
          ? travelAllowanceTypes
          : counterTravelAllowanceType
        )?.filter?.(Boolean) ?? [],
    },
    enableReinitialize: true,
    validationSchema: isSelfService ? SelfApproveValidator() : Validator(),
    onSubmit,
  });

  const promoBonusTotal = useMemo(() => {
    let hours = { weekdayHours: 0, weekendHours: 0 };

    if (totalShifts?.length) {
      hours = getLongTermHours({
        shifts: totalShifts,
        weekdayRate: formik.values?.pharmacyWeekDayHourlyRate ?? 0,
        weekendRate: formik.values?.pharmacyWeekendHourlyRate ?? 0,
        credit: professionalCreditsLeft ?? 0,
      });
    }

    return (hours.weekdayHours + hours.weekendHours) * (multiHourlyBonus ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values?.pharmacyWeekDayHourlyRate,
    formik.values?.pharmacyWeekendHourlyRate,
    professionalCreditsLeft,
    multiHourlyBonus,
  ]);

  const promoTotal = useMemo(() => {
    let hours = { weekdayHours: 0, weekendHours: 0 };

    if (totalShifts?.length) {
      hours = getLongTermHours({
        shifts: totalShifts,
        weekdayRate: formik.values?.pharmacyWeekDayHourlyRate ?? 0,
        weekendRate: formik.values?.pharmacyWeekendHourlyRate ?? 0,
        credit: creditsLeft ?? 0,
      });
    }
    const weekdayDiscount = (pharmacyWeekday ?? 0) - (multiPromoHourlyRate ?? 0);
    const weekendDiscount = (pharmacyWeekend ?? 0) - (multiPromoHourlyRate ?? 0);

    return (
      weekdayDiscount * hours.weekdayHours
      + weekendDiscount * hours.weekendHours
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values?.pharmacyWeekDayHourlyRate,
    formik.values?.pharmacyWeekendHourlyRate,
    creditsLeft,
    multiPromoHourlyRate,
  ]);

  useSyncWorkshiftValues(formik);

  useEffect(() => {
    formik.setFieldValue(meal, !!(mealAllowance || counterMealAllowance));
    formik.setFieldValue(
      travel,
      !!(travelAllowanceTypes?.length || counterTravelAllowanceType?.length),
    );
    formik.setFieldValue(
      accommodation,
      !!(accommodationAllowanceType || counterAccommodationAllowanceType),
    );
    formik.setFieldValue(emergency, !!(emergencyFees || counterEmergencyFees));
    formik.setFieldValue(others, !!otherAllowance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      // eslint-disable-next-line react-hooks/exhaustive-deps
      },
    [],
  );

  return {
    details,
    formik,
    creditsLeft,
    pharmacyInfoText,
    professionalInfoText,
    promoTotal,
    promoBonusTotal,
    hourlyBonus,
    professionalCreditsLeft,
  };
};

export default useApproveBooking;
